import { useState, useEffect } from 'react';
import { Rocket, ExternalLink, Check, Copy } from 'lucide-react';
import { useIntegrations } from '@/hooks/useIntegrations';
import { PxMark } from '@/components/PxMark';
import { cn } from '@/lib/utils';

interface DeployStatusPopoverProps {
    projectId: string;
    projectName: string;
    files: Array<{ name: string; content: string }>;
}

type DeploymentStatus = 'never' | 'deploying' | 'deployed';

export function DeployStatusPopover({ projectId, projectName, files }: DeployStatusPopoverProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    const {
        deployToVercelClaim,
        fetchProjectIntegrations,
        projectIntegrations,
        isDeploying,
    } = useIntegrations();

    // Get existing Vercel integration
    const vercelIntegration = projectIntegrations?.find(i => i.provider === 'vercel');
    const deployedUrl = vercelIntegration?.external_project_url;
    const vercelProjectId = vercelIntegration?.external_project_id;

    // Determine status
    const status: DeploymentStatus = isDeploying
        ? 'deploying'
        : deployedUrl
            ? 'deployed'
            : 'never';

    // Fetch integrations on mount
    useEffect(() => {
        if (projectId) {
            fetchProjectIntegrations(projectId);
        }
    }, [projectId, fetchProjectIntegrations]);

    const handleDeploy = async () => {
        try {
            await deployToVercelClaim(projectId, projectName, files, vercelProjectId);
            await fetchProjectIntegrations(projectId);
        } catch (error) {
            console.error('Deploy error:', error);
        }
    };

    const handleCopyUrl = () => {
        if (deployedUrl) {
            navigator.clipboard.writeText(deployedUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    // Grammaire OFFSET : dot sémantique + label mono pour chaque état
    const statusDot: Record<DeploymentStatus, string> = {
        never: 'bg-faint',
        deploying: 'bg-warn',
        deployed: 'bg-ok',
    };

    const statusLabel: Record<DeploymentStatus, string> = {
        never: 'Non déployé',
        deploying: 'Déploiement…',
        deployed: 'Déployé',
    };

    const statusColor: Record<DeploymentStatus, string> = {
        never: 'text-faint hover:text-foreground',
        deploying: 'text-warn',
        deployed: 'text-ok',
    };

    return (
        <div className="relative">
            {/* Déclencheur */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={cn(
                    "flex items-center gap-1.5 p-1.5 transition-colors",
                    isOpen ? "bg-secondary" : "hover:bg-secondary",
                    statusColor[status]
                )}
                title={statusLabel[status]}
            >
                <Rocket className="h-3.5 w-3.5" />
                {status === 'deployed' && <span className="h-1.5 w-1.5 rounded-full bg-ok" />}
            </button>

            {/* Panneau */}
            {isOpen && (
                <>
                    <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
                    <div className="absolute right-0 top-full z-20 mt-2 w-72 border border-hairline-strong bg-popover py-2">
                        {/* En-tête : dot sémantique + label mono */}
                        <div className="mb-2 border-b border-border px-3 pb-2">
                            <div className="flex items-center gap-2">
                                <span className={cn("h-1.5 w-1.5 rounded-full", statusDot[status])} />
                                <span className="font-mono text-[11px] uppercase tracking-wider text-foreground">
                                    {statusLabel[status]}
                                </span>
                            </div>
                        </div>

                        {/* Contenu */}
                        <div className="space-y-3 px-3">
                            {status === 'deployed' && deployedUrl && (
                                <>
                                    {/* URL déployée — row mono copiable */}
                                    <div className="flex items-center gap-2 border border-border bg-secondary p-2">
                                        <span className="flex-1 truncate font-mono text-[11px] text-muted-foreground">
                                            {deployedUrl.replace(/^https?:\/\//, '')}
                                        </span>
                                        <button
                                            onClick={handleCopyUrl}
                                            className="p-1 transition-colors hover:bg-surface-3"
                                            title="Copier"
                                        >
                                            {copied ? (
                                                <Check className="h-3 w-3 text-ok" />
                                            ) : (
                                                <Copy className="h-3 w-3 text-faint" />
                                            )}
                                        </button>
                                        <a
                                            href={deployedUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="p-1 transition-colors hover:bg-surface-3"
                                            title="Ouvrir"
                                        >
                                            <ExternalLink className="h-3 w-3 text-faint" />
                                        </a>
                                    </div>

                                    {/* Redéployer — secondaire */}
                                    <button
                                        onClick={handleDeploy}
                                        disabled={isDeploying}
                                        className="flex w-full items-center justify-center gap-2 border border-border bg-card px-3 py-2 text-xs font-medium text-foreground transition-colors hover:bg-secondary disabled:opacity-40"
                                    >
                                        {isDeploying ? (
                                            <>
                                                <PxMark state="generating" size={10} />
                                                Déploiement…
                                            </>
                                        ) : (
                                            <>
                                                <Rocket className="h-3 w-3" />
                                                Redéployer
                                            </>
                                        )}
                                    </button>
                                </>
                            )}

                            {status === 'deploying' && (
                                <div className="flex flex-col items-center gap-3 py-4">
                                    <PxMark state="generating" size={20} />
                                    <p className="text-xs text-muted-foreground">Build en cours sur Vercel…</p>
                                </div>
                            )}

                            {status === 'never' && (
                                <>
                                    <p className="text-xs text-muted-foreground">
                                        Ce projet n'a jamais été déployé. Déployez-le pour obtenir une URL publique.
                                    </p>
                                    {/* CTA primaire encre — jamais de dégradé */}
                                    <button
                                        onClick={handleDeploy}
                                        disabled={isDeploying}
                                        className="flex w-full items-center justify-center gap-2 bg-primary px-3 py-2.5 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-40"
                                    >
                                        <Rocket className="h-3 w-3" />
                                        {isDeploying ? 'Déploiement…' : 'Déployer maintenant'}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
